import { Group, Object3D, Ray, Sphere } from "three";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { Jaco3d } from "oc-jaco3d/arrangement3d/Jaco3d";
import { OcAxisHelper } from "oc-threejs/objects/OcAxisHelper";
import { v3 } from "oc-threejs/short3";
import { Images } from "oc-canvas2d/Images";
import * as Maths3 from "oc-threejs/Maths3";
import * as Debugs from "oc-threejs/Debugs";
import { colorFromStyle, createUnitBoxMesh } from "../commons/Utils";
import * as Scenes from "oc-threejs/Scenes";
import * as Textures from "oc-threejs/Textures";
import lockOpenIcon from "../style/icons/lock-open-solid.svg";
import lockClosedIcon from "../style/icons/lock-solid.svg";
import { Part } from "../arrangement/Part";

/**
 * @abstact
 */
export class AbstractPart3d extends Jaco3d {
  raycastBox: Object3D = null;
  selectBox: Object3D = null;
  hoverBox: Object3D = null;
  debugObjects: Object3D = null;
  lock: Group = null;

  declare jaco: Jaco;

  _selected = false;

  constructor(jaco: Jaco) {
    super(jaco);
    this.tapable = true;

    this.raycastBox = createUnitBoxMesh(APP.materials.create("invisible"), "raycastBox");
    this.raycastBox.hoverable = true;
    (this.raycastBox as any).receivesMouseRaycasts = true;
    this.add(this.raycastBox);
    this.raycastBox.intersectionPriority = 1;
    this.selectBox = createUnitBoxMesh(APP.materials.create("materialSelect"), "selectBox");
    (this.selectBox as any).receivesMouseRaycasts = false;
    this.selectBox.visible = false;
    this.add(this.selectBox);
    this.hoverBox = createUnitBoxMesh(APP.materials.create("materialPartHover"), "hoverBox");
    (this.hoverBox as any).receivesMouseRaycasts = false;
    this.hoverBox.visible = false;
    this.hoverBox.renderOrder = 999;
    this.add(this.hoverBox);
    this.raycastBox.onChangeInDragSelectHoverState = () => {
      const hovering =
        (this.raycastBox.hovering || ((this.jaco as any).hovered ?? false)) && !this._selected;
      if (this.hoverBox.visible !== hovering) {
        this.hoverBox.visible = hovering;
        STAGE.dirty();
      }
    };
  }

  update3d() {
    super.update3d();

    const box = this.getBox();
    const center = box.getCenter(v3());
    const size = box.getSize(v3());
    this.raycastBox.scale.copy(
      v3()
        .copy(size)
        .add(v3(0, 0.05, 0.05))
    );
    this.raycastBox.position.copy(center);
    this.selectBox.scale.copy(
      v3()
        .copy(size)
        .add(v3(0.2, 0, 1))
        .setY(0.02)
    );
    this.selectBox.position.copy(center).setY(-0.025);
    this.hoverBox.scale.copy(
      v3()
        .copy(size)
        .add(v3(0.05, 0.05, 0.05))
    );
    this.hoverBox.position.copy(center);
    if (this.changed("hovered")) {
      this.raycastBox.onChangeInDragSelectHoverState();
    }

    this.maybeUpdateDebugObjects();
    this.showIndividualisiertLock();
  }

  maybeUpdateDebugObjects() {
    if (APP.debugMode) {
      Scenes.remove(this.debugObjects);

      this.debugObjects = new Group();
      this.debugObjects.name = "debugObjects";
      this.add(this.debugObjects);

      this.debugObjects.add(new OcAxisHelper());
      const snapDirection = Debugs.ray(
        new Ray(this.getBox().grid(0.5, 1.2, 0.5), v3(0, 0.5, 0)),
        0x000000
      );
      this.debugObjects.add(snapDirection);
      snapDirection.quaternion.copy(
        (this.jaco as Part).snap == 0
          ? Maths3.quaternion.zero
          : (this.jaco as Part).snap == +1
            ? Maths3.quaternion.z270
            : Maths3.quaternion.z90
      );

      const idLabel = Textures.createTextSprite(`${this.jaco.id}`, {
        moveTextUpinHeightMultiplies: 1,
        cameraIndependendSize: false,
        orginalHeight: 0.1,
      });
      idLabel.position.copy(this.getBox().grid(0.5, 1.1, 0.5));
      this.debugObjects.add(idLabel);
    }
  }

  async showIndividualisiertLock() {
    Scenes.remove(this.lock);
    this.lock = null;

    if (!(this.jaco as any).isPfosten && !(this.jaco as any).isElement) {
      return;
    }

    const agt = APP.arrangement;
    const recentlyIndividualisiertAuswahlwert = (
      agt.recentlyIndividualisiertAuswahlwert ?? ""
    ).concat("Individualisiert");
    const lockClosed =
      this.jaco[recentlyIndividualisiertAuswahlwert] ||
      (this.jaco as any).hasIndividualisierteAuswahlwerte;

    if (
      !APP?.isTorkonfigurator &&
      lockClosed &&
      (((this.jaco as any).showIndividualisiert && (this.jaco as any).selected) ||
        agt.showAllIndividualisiert)
    ) {
      const lock = (this.lock = new Group());
      (lock as any).isIndividualisierungLockIcon = true;
      lock.quaternion.copy(Maths3.quaternion.x270);
      this.add(lock);

      const color = "#" + colorFromStyle("--color-dark-2").getHexString();
      const options = {
        supportMultiLine: true,
        cameraIndependendSize: false,
        color,
        orginalHeight: 0.075,
        backgroundColor: "transparent",
      };

      const lockClosedPlane = Textures.createImagePlane(await Images.loadImage(lockClosedIcon), {
        pixelPerUnit: 1250,
      });
      lockClosedPlane.quaternion.copy(Maths3.quaternion.x90);
      lock.add(lockClosedPlane);

      const label = Textures.createTextSprite(I18N("Individualisierung aufheben"), {
        ...options,
        backgroundColor: "white",
      });
      label.visible = false;
      label.position.set(0, -0.4, 0.2);
      lock.add(label);

      lock.hoverable = true;
      lock.tapable = true;
      (lock as any).receivesMouseRaycasts = true;
      lock.onChangeInDragSelectHoverState = () => {
        label.visible = lock.hovering;
        STAGE.dirty();
      };

      lock.onTap = async () => {
        // Alle Individualisierungs-Flags des Elements/Pfosten zurücksetzen
        if ((this.jaco as any).isElement) {
          (this.jaco as any).füllungAuswahlwertIndividualisiert = false;
          (this.jaco as any).abschlussleisteAuswahlwertIndividualisiert = false;
          (this.jaco as any).profilAuswahlwertIndividualisiert = false;
          (this.jaco as any).profilPositionIndividualisiert = false;
        } else if ((this.jaco as any).isPfosten) {
          (this.jaco as any).pfostenfarbeAuswahlwertIndividualisiert = false;
          (this.jaco as any).bodenbefestigungAuswahlwertIndividualisiert = false;
          (this.jaco as any).pfostenartAuswahlwertIndividualisiert = false;
          (this.jaco as any).elementhalterAuswahlwertIndividualisiert = false;
        }

        lock.remove(lockClosedPlane);
        const lockOpenPlan = Textures.createImagePlane(await Images.loadImage(lockOpenIcon), {
          pixelPerUnit: 1250,
        });
        lockOpenPlan.quaternion.copy(Maths3.quaternion.x90);
        lock.add(lockOpenPlan);
        // openLockIcon nach 1s wieder ausblenden
        setTimeout(() => {
          (this.jaco as any).showIndividualisiert = false;
          APP.dirty();
        }, 1000);
      };

      this.lock?.position.copy(
        this.jaco
          .getBox()
          .grid(0.5, 1, 0)
          .add(v3(0, 0.3, 0))
      );
    }
  }

  getWorldSphere() {
    return this.getWorldBox().getBoundingSphere(new Sphere());
  }

  getWorldBox() {
    return this.getBox().applyMatrix4(this.matrixWorld);
  }

  getBox() {
    return this.jaco.getBox();
  }

  getSize() {
    return this.jaco.getSize();
  }

  getMeta() {
    return this.jaco.getMeta();
  }

  _updateState() {
    this.selectBox.visible = this._selected;
  }

  get selected() {
    return this._selected;
  }

  set selected(v: boolean) {
    this._selected = v;
    this._updateState();
  }
}
